import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { MyGroupedExpensablesDataTableQuery } from 'generated/resources/datatable.graphql'
import { GroupDetailFragment } from 'generated/resources/graphql'

import DataTable, { useDataTable } from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Options } from 'modules/expenses/components/expenses/DataTable/_Actions/Options'
import type { Expensable } from 'modules/expenses/components/expenses/DataTable/types'
import { ActionFn } from 'modules/expenses/components/expenses/DataTable/useActions'
import { AddButton } from 'modules/expenses/components/expenses/New/AddButton'
import { useExportPdf } from 'modules/expenses/hooks/useGroup'
import {
  canAddExpensableToGroup,
  canAddExpensableToPayment,
  canAddExpensableToPayroll,
  canRemoveExpensableFromGroup,
  canRemoveExpensableFromPayroll,
  canReviewExpensable,
} from 'modules/expenses/lib/expensables/availableActions'
import {
  everyReimbursableCurrencyAllowed,
  showNotAllowedCurrenciesMessage,
} from 'modules/expenses/lib/expensables/expensable'
import { isDraftGroup } from 'modules/expenses/lib/groups/group'
import { useMyExpenses } from 'modules/expenses/pages/my_expenses/MyExpensesContext'

interface HeaderProps {
  group: GroupDetailFragment
  search: string
  onApprove: ActionFn
  onReject: ActionFn
  onRestartReview: ActionFn
  onAddToPayroll: ActionFn
  onAddToPayment: ActionFn
  onRemoveFromPayroll: ActionFn
  onSetToPaid: ActionFn
  onRevertPaid: ActionFn
  onAddToGroup: ActionFn
  onRemoveFromGroup: ActionFn
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void
  canCreateExpenses: boolean
}

export const Header = ({
  group,
  onApprove,
  onReject,
  onAddToPayroll,
  onAddToPayment,
  onRemoveFromPayroll,
  onAddToGroup,
  onRemoveFromGroup,
  canCreateExpenses,
}: HeaderProps) => {
  const { employee, company } = useCurrentSession()
  const { data, selectedRows } = useDataTable<MyGroupedExpensablesDataTableQuery, Expensable>()
  const { featureAvailabilities } = useMyExpenses()
  const { exportPdf, loading: exportingPdf } = useExportPdf()

  const selectedIds = [...selectedRows].map(Number)
  const selectedExpensables = data.filter(({ id }) => selectedIds.includes(id))

  const availableActions = {
    canReview: selectedExpensables.some((expensable) =>
      canReviewExpensable(expensable, 'personal')
    ),
    canAddToGroup: selectedExpensables.some((expensable) =>
      canAddExpensableToGroup(
        featureAvailabilities.expensableGroups,
        expensable,
        'personal',
        employee
      )
    ),
    canRemoveFromGroup: selectedExpensables.some((expensable) =>
      canRemoveExpensableFromGroup(expensable, 'personal')
    ),
    canRemoveFromPayroll: selectedExpensables.some((expensable) =>
      canRemoveExpensableFromPayroll(expensable, featureAvailabilities.reimburseThroughPayroll)
    ),
    canAddToPayroll: selectedExpensables.some((expensable) =>
      canAddExpensableToPayroll(expensable, featureAvailabilities.reimburseThroughPayroll)
    ),
    canAddToPayment: selectedExpensables.some((expensable) =>
      canAddExpensableToPayment(expensable, featureAvailabilities.reimburseThroughSepa)
    ),
  }

  const handleApprove = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canReviewExpensable(expensable, 'personal')
    )

    onApprove(validExpensables)
  }

  const handleReject = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canReviewExpensable(expensable, 'personal')
    )

    onReject(validExpensables)
  }

  const handleRemoveFromPayroll = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canRemoveExpensableFromPayroll(expensable, featureAvailabilities.reimburseThroughPayroll)
    )

    onRemoveFromPayroll(validExpensables)
  }

  const handleAddToPayroll = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canAddExpensableToPayroll(expensable, featureAvailabilities.reimburseThroughPayroll)
    )

    if (!everyReimbursableCurrencyAllowed(validExpensables)) {
      return showNotAllowedCurrenciesMessage()
    }

    onAddToPayroll(validExpensables)
  }

  const handleAddToPayment = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canAddExpensableToPayment(expensable, featureAvailabilities.reimburseThroughSepa)
    )

    onAddToPayment(validExpensables)
  }

  const handleAddToGroup = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canAddExpensableToGroup(
        featureAvailabilities.expensableGroups,
        expensable,
        'personal',
        employee
      )
    )

    onAddToGroup(validExpensables)
  }

  const handleRemoveFromGroup = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canRemoveExpensableFromGroup(expensable, 'personal')
    )

    onRemoveFromGroup(validExpensables)
  }

  const handleExportPDF = async () => {
    const handled = handleResult(await exportPdf(group?.id, company.id), () => null)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('expenses_v2.group_detail.flash_messages.export_pdf.success'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('expenses_v2.group_detail.flash_messages.export_pdf.error'),
      })
    }
  }

  return (
    <Box gap="s16">
      <DataTable.HeadBar
        disableExport
        actions={
          <>
            {isDraftGroup(group) && canCreateExpenses && (
              <AddButton type="secondary" canUsePerDiems={featureAvailabilities.canUsePerDiems} />
            )}
            {selectedExpensables.length > 0 && (
              <PopoverMenu type="list">
                <PopoverMenu.Trigger>
                  <Button.Decorative type="secondary">{i18n.t('common.actions')}</Button.Decorative>
                </PopoverMenu.Trigger>
                <PopoverMenu.Content side="bottom" align="end">
                  <Options
                    {...availableActions}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onRemoveFromPayroll={handleRemoveFromPayroll}
                    onAddToPayroll={handleAddToPayroll}
                    onAddToPayment={handleAddToPayment}
                    onAddToGroup={handleAddToGroup}
                    onRemoveFromGroup={handleRemoveFromGroup}
                  />
                </PopoverMenu.Content>
              </PopoverMenu>
            )}
          </>
        }
        customPopoverOptions={
          <PopoverMenu.TouchableOption
            onClick={() => !exportingPdf && handleExportPDF()}
            title={i18n.t('expenses_v2.buttons.export_to_pdf')}
          />
        }
      />
    </Box>
  )
}
